/**
 * Commission Engine
 * 
 * Calculates rep compensation exclusively from immutable Appointment Result records.
 * No manual overrides. No commission without a confirmed "sold" outcome.
 */

import type { AppointmentResult } from "./appointmentResultTypes";
import type { Job } from "./types";
import { calculateCloseRate } from "@/lib/metricsUtils";
import { getDisplayPrice } from "./appointmentResultSelectors";

// ── Configuration ──

export const BASE_COMMISSION_RATE = 0.08; // 8% of revenue

export const CLOSE_RATE_BONUSES = [
  { threshold: 0.50, bonus: 0.03, label: "Elite (50%+)" },
  { threshold: 0.40, bonus: 0.02, label: "Strong (40%+)" },
  { threshold: 0.30, bonus: 0.01, label: "Solid (30%+)" },
] as const;

export const SAME_DAY_CLOSE_BONUS = 0.005;   // +0.5%
export const FINANCING_BONUS_FLAT = 150;       // $150 per financed deal
export const RECOVERY_BONUS_FLAT = 200;        // $200 per follow-up converted sale

// ── Types ──

export interface DealCommission {
  resultId: string;
  customerName: string;
  appointmentDate: string;
  revenue: number;
  baseRate: number;
  baseAmount: number;
  sameDayBonus: number;
  financingBonus: number;
  recoveryBonus: number;
  totalDeal: number;
}

export interface RepScorecard {
  repName: string;
  // Performance
  appointmentsRan: number;
  demosCompleted: number;
  noDemos: number;
  salesClosed: number;
  noSales: number;
  rescheduled: number;
  closeRate: number;        // sold / (sold + noSale)
  demoToSaleRate: number;   // sold / demosCompleted
  appointmentToSaleRate: number; // sold / total
  // Financial
  totalRevenueSold: number;
  averageTicket: number;
  financingCount: number;
  cashCount: number;
  financingMix: number;     // % financing
  // Discipline
  resultSubmissionRate: number; // always 100% since results are required
  followUpCompletionRate: number;
  rescheduleRecoveryRate: number;
  // Commission
  baseCommissionRate: number;
  closeRateBonusRate: number;
  closeRateBonusLabel: string;
  effectiveRate: number;
  deals: DealCommission[];
  totalBaseCommission: number;
  totalSameDayBonus: number;
  totalFinancingBonus: number;
  totalRecoveryBonus: number;
  totalCloseRateBonus: number;
  totalEarned: number;
  // Tier
  performanceTier: "elite" | "strong" | "solid" | "developing";
}

// ── Engine ──

export function computeRepScorecard(
  repName: string,
  results: AppointmentResult[],
  followUpCompletionRate = 1.0,
  jobs?: Job[],
): RepScorecard {
  const repResults = results.filter(r => r.assignedRep === repName);

  const sold = repResults.filter(r => r.resultType === "sold");
  const noSale = repResults.filter(r => r.resultType === "no_sale");
  const rescheduled = repResults.filter(r => r.resultType === "rescheduled");
  const noDemo = repResults.filter(r => r.resultType === "no_demo");
  const closeRate = calculateCloseRate(repResults);
  const demoToSaleRate = closeRate;
  const appointmentToSaleRate = repResults.length > 0 ? sold.length / repResults.length : 0;

  // Financial — canonical: SOLD revenue from Job.financials.contractValue when linked.
  const totalRevenueSold = sold.reduce((s, r) => s + getDisplayPrice(r, jobs), 0);
  const averageTicket = sold.length > 0 ? totalRevenueSold / sold.length : 0;
  const financingCount = sold.filter(r => r.pricingSnapshot?.paymentMethod === "financing").length;
  const cashCount = sold.filter(r => r.pricingSnapshot?.paymentMethod !== "financing").length;
  const financingMix = sold.length > 0 ? financingCount / sold.length : 0;

  // Discipline
  const rescheduleRecoveryRate = rescheduled.length > 0
    ? sold.filter(r => {
        // Check if there's a rescheduled result for the same lead that preceded this sale
        return rescheduled.some(rs =>
          rs.leadId === r.leadId &&
          new Date(rs.submittedAt) < new Date(r.submittedAt)
        );
      }).length / rescheduled.length
    : 1.0;

  // Commission: close rate bonus
  let closeRateBonusRate = 0;
  let closeRateBonusLabel = "None";
  for (const tier of CLOSE_RATE_BONUSES) {
    if (closeRate >= tier.threshold) {
      closeRateBonusRate = tier.bonus;
      closeRateBonusLabel = tier.label;
      break;
    }
  }

  const effectiveRate = BASE_COMMISSION_RATE + closeRateBonusRate;

  // Per-deal commission (only sold outcomes — revenue from canonical resolver)
  const deals: DealCommission[] = sold
    .filter(r => getDisplayPrice(r, jobs) > 0)
    .map(r => {
      const revenue = getDisplayPrice(r, jobs);
      const baseAmount = Math.round(revenue * effectiveRate);
      const isSameDay = r.pricingSnapshot?.paymentMethod === "cash";
      const isFinancing = r.pricingSnapshot?.paymentMethod === "financing";
      const isRecovery = rescheduled.some(rs =>
        rs.leadId === r.leadId && new Date(rs.submittedAt) < new Date(r.submittedAt)
      );

      const sameDayBonus = isSameDay ? Math.round(revenue * SAME_DAY_CLOSE_BONUS) : 0;
      const financingBonus = isFinancing ? FINANCING_BONUS_FLAT : 0;
      const recoveryBonus = isRecovery ? RECOVERY_BONUS_FLAT : 0;

      return {
        resultId: r.id,
        customerName: r.customerName,
        appointmentDate: r.commissionEarnedAt || r.submittedAt || r.appointmentDatetime,
        revenue,
        baseRate: effectiveRate,
        baseAmount,
        sameDayBonus,
        financingBonus,
        recoveryBonus,
        totalDeal: baseAmount + sameDayBonus + financingBonus + recoveryBonus,
      };
    });

  const totalBaseCommission = deals.reduce((s, d) => s + d.baseAmount, 0);
  const totalSameDayBonus = deals.reduce((s, d) => s + d.sameDayBonus, 0);
  const totalFinancingBonus = deals.reduce((s, d) => s + d.financingBonus, 0);
  const totalRecoveryBonus = deals.reduce((s, d) => s + d.recoveryBonus, 0);
  const totalCloseRateBonus = Math.round(totalRevenueSold * closeRateBonusRate);
  const totalEarned = deals.reduce((s, d) => s + d.totalDeal, 0);

  // Tier
  let performanceTier: RepScorecard["performanceTier"] = "developing";
  if (closeRate >= 0.50) performanceTier = "elite";
  else if (closeRate >= 0.40) performanceTier = "strong";
  else if (closeRate >= 0.30) performanceTier = "solid";

  return {
    repName,
    appointmentsRan: repResults.length,
    demosCompleted: repResults.length - noDemo.length,
    noDemos: noDemo.length,
    salesClosed: sold.length,
    noSales: noSale.length,
    rescheduled: rescheduled.length,
    closeRate,
    demoToSaleRate,
    appointmentToSaleRate,
    totalRevenueSold,
    averageTicket,
    financingCount,
    cashCount,
    financingMix,
    resultSubmissionRate: 1.0,
    followUpCompletionRate,
    rescheduleRecoveryRate,
    baseCommissionRate: BASE_COMMISSION_RATE,
    closeRateBonusRate,
    closeRateBonusLabel,
    effectiveRate,
    deals,
    totalBaseCommission,
    totalSameDayBonus,
    totalFinancingBonus,
    totalRecoveryBonus,
    totalCloseRateBonus,
    totalEarned,
    performanceTier,
  };
}

export const TIER_CONFIG: Record<RepScorecard["performanceTier"], { label: string; color: string; bg: string }> = {
  elite:      { label: "Elite",      color: "text-success",     bg: "bg-success/10" },
  strong:     { label: "Strong",     color: "text-primary",     bg: "bg-primary/10" },
  solid:      { label: "Solid",      color: "text-warning",     bg: "bg-warning/10" },
  developing: { label: "Developing", color: "text-muted-foreground", bg: "bg-muted/50" },
};
